import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "@phosphor-icons/react";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthModal } from "@/components/AuthModal";
import { OfferModal } from "@/components/OfferModal";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);

  const makeOffer = () => {
    if (!user) { setAuthOpen(true); return; }
    setOfferOpen(true);
  };

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => setProduct(data)).catch(() => setNotFound(true));
  }, [id]);

  const acquire = async () => {
    if (!user) { setAuthOpen(true); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        product_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to begin acquisition.");
      setBusy(false);
    }
  };

  if (notFound)
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050505]">
        <p className="font-serif-lux text-3xl font-light italic text-[#52525b]">Piece not found.</p>
      </div>
    );

  if (!product)
    return <div className="min-h-screen bg-[#050505]" />;

  return (
    <div className="min-h-screen bg-[#050505] pt-24">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <button
          data-testid="back-btn"
          onClick={() => navigate("/marketplace")}
          className="mb-8 flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#a1a1aa] hover:text-[#f5f5f0]"
        >
          <ArrowLeft size={14} /> The Collection
        </button>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-20">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className="relative overflow-hidden bg-[#0a0a0a]"
          >
            <img
              data-testid="product-image"
              src={product.image}
              alt={product.title}
              className="h-full max-h-[80vh] w-full object-cover"
            />
            {product.sold && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70">
                <span className="font-serif-lux text-4xl italic text-[#f5f5f0]">Acquired</span>
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.19, 1, 0.22, 1] }}
            className="flex flex-col justify-center py-6"
          >
            <div className="flex items-center gap-3">
              <span className="border border-gold/40 px-3 py-1 text-[9px] uppercase tracking-[0.3em] text-gold">
                {product.edition}
              </span>
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#52525b]">{product.category}</span>
            </div>
            <p className="mt-6 text-[11px] uppercase tracking-[0.3em] text-[#a1a1aa]">{product.maker}</p>
            <h1 data-testid="product-title" className="font-serif-lux mt-3 text-5xl font-light leading-none text-[#f5f5f0] md:text-7xl">
              {product.title}
            </h1>
            <p className="mt-8 max-w-md text-base font-light leading-relaxed text-[#a1a1aa]">
              {product.description}
            </p>

            <dl className="mt-10 grid grid-cols-2 gap-y-6 border-t border-white/10 pt-8 text-sm">
              <div>
                <dt className="text-[10px] uppercase tracking-[0.25em] text-[#52525b]">Year</dt>
                <dd className="mt-1 font-light text-[#f5f5f0]">{product.year}</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase tracking-[0.25em] text-[#52525b]">Provenance</dt>
                <dd className="mt-1 font-light text-[#f5f5f0]">{product.provenance}</dd>
              </div>
            </dl>

            <div className="mt-12 flex flex-col gap-6 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#52525b]">Price</p>
                <p data-testid="product-price" className="font-serif-lux text-4xl font-light text-[#f5f5f0]">
                  {money(product.price, product.currency)}
                </p>
              </div>
              <button
                data-testid="acquire-btn"
                onClick={acquire}
                disabled={busy || product.sold}
                className="hover-invert rounded-none border border-[#f5f5f0] bg-[#f5f5f0] px-10 py-4 text-[11px] uppercase tracking-[0.3em] text-black hover:bg-transparent hover:text-[#f5f5f0] disabled:cursor-not-allowed disabled:opacity-40"
              >
                {product.sold ? "Acquired" : busy ? "Preparing…" : user ? "Acquire this piece" : "Sign in to acquire"}
              </button>
            </div>
            {!product.sold && (
              <button
                data-testid="make-offer-btn"
                onClick={makeOffer}
                className="mt-4 text-[11px] uppercase tracking-[0.3em] text-[#a1a1aa] underline underline-offset-8 hover:text-gold"
              >
                Or make a private offer
              </button>
            )}
          </motion.div>
        </div>
      </div>
      <div className="h-32" />
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} defaultMode="register" />
      <OfferModal open={offerOpen} onOpenChange={setOfferOpen} product={product} />
    </div>
  );
}
